'use client';

import { Alert, IconButton, Stack, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';

import { Icon } from '@/components/shared/icon';

export const OfflineBanner = () => {
  const [state, setState] = useState<'online' | 'offline' | 'dismissed'>(
    'online'
  );

  const handleOnline = useCallback(() => {
    setState('online');
  }, []);

  const handleOffline = useCallback(() => {
    setState((prev) => {
      // If dismissed, don't show until we go online and back offline
      if (prev === 'online') {
        return 'offline';
      }
      return prev;
    });
  }, []);

  const handleDismiss = useCallback(() => {
    setState('dismissed');
  }, []);

  useEffect(() => {
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [handleOnline, handleOffline]);

  const isVisible = state === 'offline';

  if (!isVisible) {
    return null;
  }

  return (
    <Alert
      severity='error'
      variant='filled'
      icon={
        <i
          className='material-symbols-error-outline-rounded'
          style={{
            // eslint-disable-next-line @eon/no-manual-typography
            fontSize: '24px',
            color: 'inherit',
          }}
        />
      }
      sx={{
        position: 'fixed',
        top: '100px',
        left: 0,
        right: 0,
        marginInline: 'auto',
        width: 'fit-content',
        zIndex: 9999,
        minWidth: '320px',
        maxWidth: '500px',
        boxShadow: 'var(--mui-customShadows-lg)',
        borderRadius: 'var(--mui-shape-customBorderRadius-lg)',
        padding: '12px 16px',
        animation: 'slideDown 0.3s ease-out',
        '@keyframes slideDown': {
          from: {
            transform: 'translateY(-100%)',
            opacity: 0,
          },
          to: {
            transform: 'translateY(0)',
            opacity: 1,
          },
        },
        '& .MuiAlert-icon': {
          alignItems: 'center',
        },
        '& .MuiAlert-action': {
          alignItems: 'center',
        },
      }}
      action={
        <IconButton
          aria-label='dismiss'
          onClick={handleDismiss}
          sx={{
            color: 'inherit',
            borderRadius: 'var(--mui-shape-customBorderRadius-sm)',
            padding: '0px',
            '&.MuiIconButton-root:hover': {
              backgroundColor: 'var(--mui-palette-action-focus)',
            },
          }}
        >
          <Icon iconClass='material-symbols-close-rounded' color='inherit' />
        </IconButton>
      }
    >
      <Stack
        sx={{
          gap: '2px',
        }}
      >
        <Typography
          variant='subtitle2Strong'
          sx={{
            color: 'inherit',
          }}
        >
          No Internet Connection
        </Typography>
        <Typography
          variant='subtitle2'
          sx={{
            color: 'inherit',
            opacity: 0.9,
          }}
        >
          Please check your network connection
        </Typography>
      </Stack>
    </Alert>
  );
};
